import { useState, useEffect } from "react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * Themed confirmation dialog. Two modes:
 *  - simple: single "Confirm" button
 *  - typed:  user must type `requireText` exactly before Confirm becomes enabled.
 *            Great for irreversible actions (delete a store, wipe uploads).
 */
export default function ConfirmDialog({
  open, onOpenChange,
  title = "Are you sure?",
  description = "",
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  requireText = "",
  requireHelp = "",
  onConfirm,
  testid = "confirm-dialog",
}) {
  const [typed, setTyped] = useState("");
  useEffect(() => { if (!open) setTyped(""); }, [open]);
  const canConfirm = requireText ? typed === requireText : true;
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="border-border" data-testid={testid}>
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display text-2xl">{title}</AlertDialogTitle>
          {description && (
            <AlertDialogDescription className="text-sm text-muted-foreground">
              {description}
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>
        {requireText && (
          <div className="space-y-1.5">
            <Label className="text-xs">
              {requireHelp || <>Type <span className="font-mono text-foreground">{requireText}</span> to confirm.</>}
            </Label>
            <Input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={requireText}
              className="font-mono"
              autoFocus
              data-testid={`${testid}-typed-input`}
            />
          </div>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`${testid}-cancel`}>{cancelLabel}</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={!canConfirm}
            className={destructive ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : ""}
            data-testid={`${testid}-ok`}
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
